// 이용권 타입 정의
export type PassType = 'free' | 'starter' | 'premium' | 'complete' | 'intelligence';

// 이용권 기능 목록
export interface PassFeature {
  id: string;
  name: string;
  description?: string;
}

// 이용권 정보 인터페이스
export interface PassInfo {
  type: PassType;
  name: string;
  description: string;
  price: number;
  youtubePrice: number;
  features: string[];
  enabled: boolean;
  highlight?: boolean; // 추천 표시
}

// Clerk 메타데이터 구조
export interface PassMetadata {
  passType: Exclude<PassType, 'free'>;
  passPurchasedAt: string; // ISO date
  passExpiresAt: string | null; // ISO date, null for starter (평생)
}

// 무료체험 메타데이터 (Clerk publicMetadata에 별도 필드로 저장)
export interface TrialMetadata {
  freeTrialStartedAt: string;   // ISO date
  freeTrialExpiresAt: string;   // ISO date
  freeTrialTier: PassType;      // 'premium'
}

// getEffectivePassType 반환 타입
export interface EffectivePassInfo {
  passType: PassType;
  isActive: boolean;
  isTrial: boolean;
  isTrialExpired: boolean;
}

// 이용권 상태
export interface PassStatus {
  passType: PassType;
  expiresAt: string | null;
  isActive: boolean;
  purchasedAt?: string;
}

// 기능 목록 정의
export const PASS_FEATURES = {
  // Free 기능
  contentLimited: '광고와 함께 모든 콘텐츠 무료 열람',
  basicCustomProblems: '기본 커스텀 문제',
  dashboard: '나만의 학습 대시보드',
  achievementBadges: '학습 도전과제 배지',
  attendanceCheck: '출석체크·연속 학습일',

  // 학습 관리 기능 (JCG-2325)
  reviewCount: '회독 수 카운트',        // Free 이상(전체)
  learningMap: '학습 경로 지도',        // Free 이상(전체)
  continueLearning: '학습 이어가기',    // 전 등급(로그인)
  studySchedule: '개인화 학습 일정',    // Starter 이상

  // Starter 기능
  adFree: '광고 제거',
  sidePanel: '풀이 사이드 패널(데스크탑)',
  tableOfContents: '목차 기능',
  noteFeature: '필기 기능',

  // Premium 기능
  problemHistory: '문제 풀이 이력',
  errorNotes: '오답 노트',
  problemSave: '문제 저장',
  difficultyControl: '문제 난이도 조절(보기 비활성화)',
  consecutiveAnswerReward: '2회 연속 정답시 보기 복사 가능',
  premiumCustomProblems: '프리미엄 커스텀 문제',
  mockExam: '프리미엄 모의고사',
  studyProgress: '공부 진행상황 체크',

  // Complete 기능
  theoryPdf: '요약PDF(이론)',
  codingPdf: '요약PDF(코딩)',

  // Intelligence 기능
  coachingAi: '코칭 AI(자동 오답노트 정리, 개인화된 추가 이론 설명)',
  autoErrorNoteOrganization: '자동 오답노트 정리',
  personalizedTheoryExplanation: '개인화된 추가 이론 설명',
} as const;

// 이용권 상수 데이터
export const PASSES: Record<PassType, PassInfo> = {
  free: {
    type: 'free',
    name: 'Free',
    description: '기본 무료 이용권',
    price: 0,
    youtubePrice: 0,
    features: [
      PASS_FEATURES.contentLimited,
      PASS_FEATURES.basicCustomProblems,
      PASS_FEATURES.dashboard,
      PASS_FEATURES.achievementBadges,
      PASS_FEATURES.attendanceCheck,
      PASS_FEATURES.reviewCount,
      PASS_FEATURES.learningMap,
      PASS_FEATURES.continueLearning,
    ],
    enabled: true,
  },
  starter: {
    type: 'starter',
    name: 'Starter',
    description: '광고 제거 + 공부 도우미',
    price: 3000,
    youtubePrice: 2000,
    features: [
      PASS_FEATURES.basicCustomProblems,
      PASS_FEATURES.dashboard,
      PASS_FEATURES.achievementBadges,
      PASS_FEATURES.attendanceCheck,
      PASS_FEATURES.reviewCount,
      PASS_FEATURES.learningMap,
      PASS_FEATURES.continueLearning,
      PASS_FEATURES.studySchedule,
      PASS_FEATURES.adFree,
      PASS_FEATURES.sidePanel,
      PASS_FEATURES.tableOfContents,
      PASS_FEATURES.noteFeature,
    ],
    enabled: true,
  },
  premium: {
    type: 'premium',
    name: 'Premium',
    description: '오답 노트 + 문제 풀기 도우미',
    price: 15000,
    youtubePrice: 13000,
    features: [
      PASS_FEATURES.basicCustomProblems,
      PASS_FEATURES.dashboard,
      PASS_FEATURES.achievementBadges,
      PASS_FEATURES.attendanceCheck,
      PASS_FEATURES.reviewCount,
      PASS_FEATURES.learningMap,
      PASS_FEATURES.continueLearning,
      PASS_FEATURES.studySchedule,
      PASS_FEATURES.adFree,
      PASS_FEATURES.sidePanel,
      PASS_FEATURES.tableOfContents,
      PASS_FEATURES.noteFeature,
      PASS_FEATURES.problemHistory,
      PASS_FEATURES.errorNotes,
      PASS_FEATURES.problemSave,
      PASS_FEATURES.difficultyControl,
      PASS_FEATURES.consecutiveAnswerReward,
      PASS_FEATURES.premiumCustomProblems,
      PASS_FEATURES.mockExam,
      PASS_FEATURES.studyProgress,
    ],
    enabled: true,
    highlight: true,
  },
  complete: {
    type: 'complete',
    name: 'Complete',
    description: '요약 PDF 포함',
    price: 30000,
    youtubePrice: 25000,
    features: [
      PASS_FEATURES.basicCustomProblems,
      PASS_FEATURES.dashboard,
      PASS_FEATURES.achievementBadges,
      PASS_FEATURES.attendanceCheck,
      PASS_FEATURES.reviewCount,
      PASS_FEATURES.learningMap,
      PASS_FEATURES.continueLearning,
      PASS_FEATURES.studySchedule,
      PASS_FEATURES.adFree,
      PASS_FEATURES.sidePanel,
      PASS_FEATURES.tableOfContents,
      PASS_FEATURES.noteFeature,
      PASS_FEATURES.problemHistory,
      PASS_FEATURES.errorNotes,
      PASS_FEATURES.problemSave,
      PASS_FEATURES.difficultyControl,
      PASS_FEATURES.consecutiveAnswerReward,
      PASS_FEATURES.premiumCustomProblems,
      PASS_FEATURES.mockExam,
      PASS_FEATURES.studyProgress,
      PASS_FEATURES.theoryPdf,
      PASS_FEATURES.codingPdf,
    ],
    enabled: true,
  },
  intelligence: {
    type: 'intelligence',
    name: 'Intelligence',
    description: '코칭 AI 포함',
    price: 40000,
    youtubePrice: 36000,
    features: [
      PASS_FEATURES.basicCustomProblems,
      PASS_FEATURES.dashboard,
      PASS_FEATURES.achievementBadges,
      PASS_FEATURES.attendanceCheck,
      PASS_FEATURES.reviewCount,
      PASS_FEATURES.learningMap,
      PASS_FEATURES.continueLearning,
      PASS_FEATURES.studySchedule,
      PASS_FEATURES.adFree,
      PASS_FEATURES.sidePanel,
      PASS_FEATURES.tableOfContents,
      PASS_FEATURES.noteFeature,
      PASS_FEATURES.problemHistory,
      PASS_FEATURES.errorNotes,
      PASS_FEATURES.problemSave,
      PASS_FEATURES.difficultyControl,
      PASS_FEATURES.consecutiveAnswerReward,
      PASS_FEATURES.premiumCustomProblems,
      PASS_FEATURES.mockExam,
      PASS_FEATURES.studyProgress,
      PASS_FEATURES.theoryPdf,
      PASS_FEATURES.codingPdf,
      PASS_FEATURES.autoErrorNoteOrganization,
      PASS_FEATURES.personalizedTheoryExplanation,
    ],
    enabled: false,
  },
};

// 모든 기능 목록 (비교 테이블용)
export const ALL_FEATURES = [
  { id: 'adFree', name: PASS_FEATURES.adFree },
  { id: 'dashboard', name: PASS_FEATURES.dashboard },
  { id: 'achievementBadges', name: PASS_FEATURES.achievementBadges },
  { id: 'attendanceCheck', name: PASS_FEATURES.attendanceCheck },
  // 학습 관리 그룹 (JCG-2325) — reviewCount가 그룹 헤더 트리거
  { id: 'reviewCount', name: PASS_FEATURES.reviewCount },
  { id: 'learningMap', name: PASS_FEATURES.learningMap },
  { id: 'continueLearning', name: PASS_FEATURES.continueLearning },
  { id: 'studySchedule', name: PASS_FEATURES.studySchedule },
  { id: 'basicCustomProblems', name: PASS_FEATURES.basicCustomProblems },
  { id: 'premiumCustomProblems', name: PASS_FEATURES.premiumCustomProblems },
  { id: 'mockExam', name: PASS_FEATURES.mockExam },
  { id: 'sidePanel', name: PASS_FEATURES.sidePanel },
  { id: 'tableOfContents', name: PASS_FEATURES.tableOfContents },
  { id: 'noteFeature', name: PASS_FEATURES.noteFeature },
  { id: 'errorNotes', name: PASS_FEATURES.errorNotes },
  { id: 'problemSave', name: PASS_FEATURES.problemSave },
  { id: 'problemHistory', name: PASS_FEATURES.problemHistory },
  { id: 'difficultyControl', name: PASS_FEATURES.difficultyControl },
  { id: 'consecutiveAnswerReward', name: PASS_FEATURES.consecutiveAnswerReward },
  { id: 'studyProgress', name: PASS_FEATURES.studyProgress },
  { id: 'theoryPdf', name: PASS_FEATURES.theoryPdf },
  { id: 'codingPdf', name: PASS_FEATURES.codingPdf },
  { id: 'autoErrorNoteOrganization', name: PASS_FEATURES.autoErrorNoteOrganization },
  { id: 'personalizedTheoryExplanation', name: PASS_FEATURES.personalizedTheoryExplanation },
];

// 이용권 순서 (상위 이용권 확인용)
export const PASS_ORDER: PassType[] = ['free', 'starter', 'premium', 'complete', 'intelligence'];

// 업그레이드 가격 정보 인터페이스
export interface UpgradePrice {
  from: PassType;
  to: PassType;
  price: number; // 비구독자 가격
  youtubePrice: number; // 구독자 가격
}

// 업그레이드 가격 상수
export const UPGRADE_PRICES: UpgradePrice[] = [
  {
    from: 'starter',
    to: 'premium',
    price: 12000, // 비구독자
    youtubePrice: 11000, // 구독자
  },
  {
    from: 'premium',
    to: 'complete',
    price: 15000, // 비구독자
    youtubePrice: 12000, // 구독자
  },
  {
    from: 'starter',
    to: 'complete',
    price: 27000, // 비구독자
    youtubePrice: 23000, // 구독자
  },
  {
    from: 'starter',
    to: 'intelligence',
    price: 37000, // 비구독자
    youtubePrice: 34000, // 구독자
  },
  {
    from: 'premium',
    to: 'intelligence',
    price: 25000, // 비구독자
    youtubePrice: 23000, // 구독자
  },
  {
    from: 'complete',
    to: 'intelligence',
    price: 10000, // 비구독자
    youtubePrice: 9000, // 구독자
  },
];

// Starter 이용권 productId
export const STARTER_PASS_PRODUCT_ID = 'starter-pass';
export const STARTER_PASS_ORDER_PREFIX = 'STARTER-';

// Premium 이용권 productId
export const PREMIUM_PASS_PRODUCT_ID = 'premium-pass';
export const PREMIUM_PASS_ORDER_PREFIX = 'PREMIUM-';
export const PREMIUM_PASS_26_2_PRODUCT_ID = 'premium-pass-26-2';
export const PREMIUM_PASS_26_3_PRODUCT_ID = 'premium-pass-26-3';

// Complete 이용권 productId
export const COMPLETE_PASS_PRODUCT_ID = 'complete-pass';
export const COMPLETE_PASS_ORDER_PREFIX = 'COMPLETE-';
export const COMPLETE_PASS_26_2_PRODUCT_ID = 'complete-pass-26-2';
export const COMPLETE_PASS_26_3_PRODUCT_ID = 'complete-pass-26-3';

// ============================================================
// 이용권 productId별 부여 등급 (JCG-2535)
// ============================================================
// 전액취소 후 권한을 재계산할 때, 과거 시즌 Complete/Premium 주문을 현재 시즌 권한으로
// 단순 승격하지 않도록 productId마다 부여 등급을 명시한다.
//   - productId ↔ 회차 매핑은 판매 일정 SSOT(src/lib/pass-sales.ts)의
//     premiumProductId/completeProductId 가 소유한다(getPassSalesSeasonForProduct).
//     여기에 seasonId 를 중복 선언하면 두 곳이 어긋나도 타입·테스트가 못 잡는다.
//   - 현재 시즌 여부·권한 유효성은 그 SSOT와 현재 시각으로 파생(isPassProductEntitlementActive)
//   - 과거 시즌 상품은 전액취소 후 권한 재계산에서 Starter 평생 권한만 잔존
//   - starter-pass: 시즌 개념 없이 평생(만료일 null)
// 값은 등급 하나뿐이라 래퍼 객체를 두지 않는다 — 빈 래퍼는 필드가 채워지며 다시 이중 관리가
// 된다(seasonId 가 그렇게 붙었다 떨어졌다, JCG-3064).
//
// 직접 인덱싱하지 말고 passTypeForProductId·isPassProductId 로 읽는다. 객체 리터럴이라
// Object.prototype 을 상속해 PASS_PRODUCT_POLICIES['constructor'] 가 함수를 돌려준다.
export const PASS_PRODUCT_POLICIES: Readonly<Record<string, Exclude<PassType, 'free'>>> = {
  [STARTER_PASS_PRODUCT_ID]: 'starter',
  [PREMIUM_PASS_PRODUCT_ID]: 'premium',
  [PREMIUM_PASS_26_2_PRODUCT_ID]: 'premium',
  [PREMIUM_PASS_26_3_PRODUCT_ID]: 'premium',
  [COMPLETE_PASS_PRODUCT_ID]: 'complete',
  [COMPLETE_PASS_26_2_PRODUCT_ID]: 'complete',
  [COMPLETE_PASS_26_3_PRODUCT_ID]: 'complete',
};

/**
 * `in` 이 아니라 hasOwnProperty 를 쓰는 이유: PASS_PRODUCT_POLICIES 는 객체 리터럴이라
 * Object.prototype 을 상속하고, `in` 은 상속 키까지 훑어 'constructor'·'toString' 을
 * 상품 ID 로 인정한다. PaymentSuccessHandler 가 URL 쿼리(사용자 제어값)를 그대로 넘기므로
 * 소유 키만 본다.
 */
export function isPassProductId(productId: string): boolean {
  return Object.prototype.hasOwnProperty.call(PASS_PRODUCT_POLICIES, productId);
}

export function passTypeForProductId(
  productId: string
): Exclude<PassType, 'free'> | null {
  return isPassProductId(productId) ? PASS_PRODUCT_POLICIES[productId] : null;
}

// Complete 상품 목록은 PASS_PRODUCT_POLICIES 에서 파생한다. 손으로 유지하면 새 회차 Complete 를
// POLICIES 에만 넣고 여기서 빠뜨렸을 때 대량메일 "Complete 보유자 제외"가 새 회차 보유자에게
// 판촉을 보낸다 — 타입·테스트 모두 못 잡는다(JCG-3064).
export const COMPLETE_PASS_PRODUCT_IDS: readonly string[] = Object.keys(
  PASS_PRODUCT_POLICIES
).filter((productId) => PASS_PRODUCT_POLICIES[productId] === 'complete');

export function isPremiumPassProductId(productId: string): boolean {
  return passTypeForProductId(productId) === 'premium';
}

export function isCompletePassProductId(productId: string): boolean {
  return passTypeForProductId(productId) === 'complete';
}

// ============================================================
// Complete 이용권 구매 시 무료 제공 PDF 설정
// ============================================================
export const COMPLETE_PASS_FREE_PDFS = [
  {
    productId: '26-3-compressed-pdf',
    goodsName: '26년 3회 이론 요약 PDF (Complete 이용권 무료 제공)',
    orderPrefix: 'GIFT-26-3-PDF-',
  },
  {
    productId: '26-3-coding-pdf',
    goodsName: '26년 3회 코딩 요약 PDF (Complete 이용권 무료 제공)',
    orderPrefix: 'GIFT-26-3-CODING-PDF-',
  },
] as const;

const LEGACY_COMPLETE_PASS_FREE_PDFS = [
  {
    productId: '26-1-compressed-pdf',
    goodsName: '26년 1·2회 통합 이론 요약 PDF (Complete 이용권 무료 제공)',
    orderPrefix: 'GIFT-PDF-',
  },
  {
    productId: '26-1-coding-pdf',
    goodsName: '26년 1·2회 통합 코딩 요약 PDF (Complete 이용권 무료 제공)',
    orderPrefix: 'GIFT-CODING-PDF-',
  },
] as const;

/** 구매한 이용권 회차에 맞는 이론·코딩 2종을 지급한다. */
export function getCompletePassFreePdfs(passProductId: string) {
  if (passProductId === COMPLETE_PASS_26_3_PRODUCT_ID) return COMPLETE_PASS_FREE_PDFS;
  if (passProductId === COMPLETE_PASS_PRODUCT_ID || passProductId === COMPLETE_PASS_26_2_PRODUCT_ID) {
    return LEGACY_COMPLETE_PASS_FREE_PDFS;
  }
  return [];
}

/**
 * Complete 이용권 회차에 실제로 포함된 무료 PDF productId 목록. (JCG-3580)
 *
 * Complete 중복구매 차단은 "구매하려는 Complete 회차에 포함된 PDF"를 이미 개별구매했는지로
 * 판정해야 한다. 다른 회차 PDF 보유는 상품 구성이 겹치지 않으므로 차단 사유가 아니다.
 */
export function getCompletePassFreePdfProductIds(
  passProductId: string
): readonly string[] {
  return getCompletePassFreePdfs(passProductId).map((pdf) => pdf.productId);
}

/** 같은 이용권에서 구판과 신판을 받아도 이론·코딩은 각각 한 종류다. */
export const COMPLETE_PASS_PDF_KINDS: Readonly<Record<string, 'theory' | 'coding'>> = {
  '26-1-compressed-pdf': 'theory',
  '26-1-coding-pdf': 'coding',
  '26-3-compressed-pdf': 'theory',
  '26-3-coding-pdf': 'coding',
};

/** @deprecated COMPLETE_PASS_FREE_PDFS 사용 */
export const COMPLETE_PASS_FREE_PDF = COMPLETE_PASS_FREE_PDFS[0];

// ============================================================
// Complete 이용권에 포함된 PDF 목록 (회차 무관 합집합)
// ============================================================
// @deprecated 회차를 구분하지 않으므로 "이 사용자의 이용권에 포함되는가" 판정에 쓰면 안 된다.
// (JCG-3580) 실제 포함 여부는 회차별 무료 지급 주문(amount=0, status='paid')이 SSOT이고,
// 회차별 구성은 getCompletePassFreePdfProductIds()로 얻는다.
export const COMPLETE_PASS_INCLUDED_PDFS = [
  '26-3-compressed-pdf',
  '26-3-coding-pdf',
  '26-1-compressed-pdf',
  '26-1-coding-pdf',
  '25-3-compressed-pdf',
] as const;

